import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { PaymentHistory } from "@/types/payment";
import { ProductTable } from "@/components/product-table";
import { AddProductDialog } from "@/components/add-product-dialog";
import { EditProductDialog } from "@/components/edit-product-dialog";
import { PaymentConfirmationDialog } from "@/components/ui/payment-confirmation-dialog";
import { PaymentInvoice } from "@/components/payment-invoice";
import { useReactToPrint } from "react-to-print";
import { useConfirm } from "@/contexts/confirm-context";
import { useAuth } from "@/contexts/auth-context";
import { useCustomerPayments } from "@/hooks/use-customer-payments";
import { useShopSettings } from "@/hooks/use-shop-settings";

import { CustomerPaymentSidebar } from "@/components/customer-payment/CustomerPaymentSidebar";
import { CustomerPaymentHeader } from "@/components/customer-payment/CustomerPaymentHeader";
import { CustomerPaymentStats } from "@/components/customer-payment/CustomerPaymentStats";
import { ProductPaymentCard } from "@/components/customer-payment/ProductPaymentCard";
import { PaymentHistoryTable } from "@/components/customer-payment/PaymentHistoryTable";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  Package,
  CreditCard,
  CheckCircle,
  Trash2,
  ChevronDown,
  ChevronUp,
  History,
  Printer,
} from "lucide-react";

interface CustomerPaymentSheetProps {
  customer: Customer | null;
  products: Product[];
  open: boolean;
  onClose: () => void;
  onAddProduct: (product: Omit<Product, "id" | "createdAt" | "remainingAmount">) => void;
  onDeleteProduct: (id: string) => Promise<void> | void;
  onUpdateProduct: (product: Product) => Promise<void> | void;
  onNavigate?: (page: "dashboard" | "customers" | "sales" | "payments" | "expenses" | "notifications" | "reports" | "users" | "settings") => void;
  hideAddProduct?: boolean;
}

export function CustomerPaymentSheet({
  customer,
  products,
  open,
  onClose,
  onAddProduct,
  onDeleteProduct,
  onUpdateProduct,
  onNavigate,
  hideAddProduct = false,
}: CustomerPaymentSheetProps) {
  const { confirm } = useConfirm();
  const { success: showToast } = useToast();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState("payment");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const [expandedCompletedProducts, setExpandedCompletedProducts] = useState<Set<string>>(new Set());
  const [globalPaymentDate] = useState<Date>(new Date());
  const [productPersistentNotes, setProductPersistentNotes] = useState<Record<string, string>>({});
  const [productNoteSaveStatus, setProductNoteSaveStatus] = useState<Record<string, 'saved' | 'unsaved' | 'saving'>>({});

  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [pendingPaymentAction, setPendingPaymentAction] = useState<{
    productsToUpdate: Product[];
    paymentsToAdd: PaymentHistory[];
    withPrint: boolean;
  } | null>(null);

  // Custom Hooks
  const { shopSettings } = useShopSettings(open);
  const {
    paymentHistory,
    setPaymentHistory,
    isLoadingPayments,
    addPaymentToDatabase,
    addBatchPaymentsToDatabase,
    deletePaymentFromDatabase,
  } = useCustomerPayments(customer, open);

  // Invoice printing state & refs
  const [invoiceData, setInvoiceData] = useState<{
    payments: PaymentHistory[];
    invoiceNumber: number;
    paymentDate: Date;
  } | null>(null);
  const invoiceRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo(0, 0);
    }
  }, [activeTab, customer?.id]);

  useEffect(() => {
    if (open) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [open]);

  useEffect(() => {
    if (customer && open && products.length > 0) {
      const notes: Record<string, string> = {};
      const status: Record<string, 'saved' | 'unsaved' | 'saving'> = {};
      products.forEach(product => {
        if (product.customerId === customer.id && product.paymentNote) {
          notes[product.id] = product.paymentNote;
          status[product.id] = 'saved';
        }
      });
      setProductPersistentNotes(notes);
      setProductNoteSaveStatus(status);
    }
  }, [customer, open, products]);

  const handlePrintInvoice = useReactToPrint({
    contentRef: invoiceRef,
    documentTitle: `وەصڵی پارەدان - ${customer?.fullName || ""}`,
    print: async (printIframe: HTMLIFrameElement) => {
      const document = printIframe.contentDocument;
      if (document) {
        const style = document.createElement('style');
        style.innerHTML = `
          @page {
            size: A4 landscape;
            margin: 5mm;
          }
          @media print {
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              zoom: 125%;
            }
            .no-print {
              display: none !important;
            }
          }
        `;
        document.head.appendChild(style);
        printIframe.contentWindow?.print();
      }
    },
    onAfterPrint: () => {
      setInvoiceData(null);
    },
  });

  const printPaymentFromHistory = useCallback((payment: PaymentHistory) => {
    if (!customer) return;

    let paymentsToPrint: PaymentHistory[];
    if (payment.invoiceNumber && payment.invoiceNumber > 0) {
      paymentsToPrint = paymentHistory.filter(p => p.invoiceNumber === payment.invoiceNumber);
    } else {
      paymentsToPrint = [payment];
    }

    setInvoiceData({
      payments: paymentsToPrint,
      invoiceNumber: payment.invoiceNumber || 0,
      paymentDate: new Date(payment.date),
    });

    setTimeout(() => {
      handlePrintInvoice();
    }, 300);
  }, [customer, paymentHistory, handlePrintInvoice]);

  const savePersistentNote = useCallback(async (productId: string, note: string) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    setProductNoteSaveStatus(prev => ({ ...prev, [productId]: 'saving' }));

    const updatedProduct: Product = {
      ...product,
      paymentNote: note || undefined,
    };

    try {
      await onUpdateProduct(updatedProduct);
      setProductPersistentNotes(prev => ({ ...prev, [productId]: note }));
      setProductNoteSaveStatus(prev => ({ ...prev, [productId]: note ? 'saved' : 'unsaved' }));
    } catch (error) {
      console.error('Failed to save note:', error);
      setProductNoteSaveStatus(prev => ({ ...prev, [productId]: 'unsaved' }));
    }
  }, [products, onUpdateProduct]);

  const handlePaySingleProduct = useCallback(async (
    payment: PaymentHistory,
    updatedProduct: Product,
    withPrint: boolean
  ) => {
    const savedPayment = await addPaymentToDatabase(payment);
    if (savedPayment) {
      setPaymentHistory((prev) => {
        if (prev.some(p => p.id === savedPayment.id)) return prev;
        return [savedPayment, ...prev];
      });

      await onUpdateProduct(updatedProduct);

      if (withPrint) {
        setInvoiceData({
          payments: [savedPayment],
          invoiceNumber: savedPayment.invoiceNumber || 0,
          paymentDate: new Date(savedPayment.date),
        });

        setTimeout(() => {
          handlePrintInvoice();
        }, 100);
      }
    }
  }, [addPaymentToDatabase, setPaymentHistory, onUpdateProduct, handlePrintInvoice]);

  const handleDeletePayment = useCallback(async (payment: PaymentHistory) => {
    const deleted = await deletePaymentFromDatabase(payment.id);
    if (deleted) {
      setPaymentHistory(prev => {
        const remainingHistory = prev.filter(p => p.id !== payment.id);
        const product = products.find(p => p.id === payment.productId);
        if (product) {
          const productRemainingPayments = remainingHistory.filter(p => p.productId === payment.productId);
          const newPaidAmount = productRemainingPayments.reduce((sum, p) => sum + p.amount, 0);
          const newRemainingAmount = Math.max(0, product.salePrice - newPaidAmount);
          const newInstallments = productRemainingPayments.length > 0
            ? Math.max(...productRemainingPayments.map(p => p.installmentNumber))
            : 0;

          const updatedProduct: Product = {
            ...product,
            paidAmount: newPaidAmount,
            remainingAmount: newRemainingAmount,
            installments: newInstallments,
          };
          onUpdateProduct(updatedProduct);
        }
        return remainingHistory;
      });
      showToast("پارەدانەکە سڕایەوە");
    }
  }, [deletePaymentFromDatabase, products, onUpdateProduct, setPaymentHistory, showToast]);

  const toggleExpanded = useCallback((productId: string) => {
    setExpandedProducts(prev => {
      const newSet = new Set<string>();
      if (!prev.has(productId)) {
        newSet.add(productId);
      }
      return newSet;
    });
  }, []);

  const toggleCompletedProductExpanded = useCallback((productId: string) => {
    setExpandedCompletedProducts(prev => {
      const newSet = new Set<string>();
      if (!prev.has(productId)) {
        newSet.add(productId);
      }
      return newSet;
    });
  }, []);

  const handleConfirmPayment = async () => {
    if (!pendingPaymentAction) return;

    const { productsToUpdate, paymentsToAdd, withPrint } = pendingPaymentAction;
    const savedPayments = await addBatchPaymentsToDatabase(paymentsToAdd);

    setPaymentHistory((prev) => {
      const existingIds = new Set(prev.map(p => p.id));
      const newPayments = savedPayments.filter(p => !existingIds.has(p.id));
      return [...newPayments, ...prev];
    });

    productsToUpdate.forEach(p => onUpdateProduct(p));
    setPendingPaymentAction(null);

    if (withPrint && savedPayments.length > 0) {
      const invoiceNumber = savedPayments[0]?.invoiceNumber || 0;
      setInvoiceData({
        payments: savedPayments,
        invoiceNumber,
        paymentDate: globalPaymentDate,
      });

      setTimeout(() => {
        handlePrintInvoice();
      }, 100);
    }
  };

  const rawCustomerProducts = useMemo(() => {
    if (!customer) return [];
    return products.filter((p) => p.customerId === customer.id);
  }, [products, customer?.id]);

  // Synchronize customer products with paymentHistory to sum all recorded payments
  const customerProducts = useMemo(() => {
    return rawCustomerProducts.map((product) => {
      const productPayments = paymentHistory.filter((pay) => pay.productId === product.id);
      if (productPayments.length === 0) return product;

      const historyPaidAmount = productPayments.reduce((sum, pay) => sum + pay.amount, 0);
      const effectivePaidAmount = historyPaidAmount;
      const effectiveRemainingAmount = Math.max(0, product.salePrice - effectivePaidAmount);
      const effectiveInstallments = Math.max(
        ...productPayments.map((p) => p.installmentNumber),
        productPayments.length
      );

      if (
        effectivePaidAmount !== product.paidAmount ||
        effectiveRemainingAmount !== product.remainingAmount ||
        effectiveInstallments !== (product.installments || 0)
      ) {
        return {
          ...product,
          paidAmount: effectivePaidAmount,
          remainingAmount: effectiveRemainingAmount,
          installments: effectiveInstallments,
        };
      }
      return product;
    });
  }, [rawCustomerProducts, paymentHistory]);

  const activeProducts = useMemo(() => customerProducts.filter((p) => p.remainingAmount > 0), [customerProducts]);
  const completedProducts = useMemo(() => customerProducts.filter((p) => p.remainingAmount <= 0), [customerProducts]);

  // Auto-persist synchronized paidAmount to database/state when paymentHistory is loaded
  useEffect(() => {
    if (!open || isLoadingPayments || paymentHistory.length === 0) return;

    customerProducts.forEach((product) => {
      const originalProduct = products.find((p) => p.id === product.id);
      if (
        originalProduct &&
        (originalProduct.paidAmount !== product.paidAmount ||
          originalProduct.remainingAmount !== product.remainingAmount ||
          originalProduct.installments !== product.installments)
      ) {
        onUpdateProduct(product);
      }
    });
  }, [open, isLoadingPayments, paymentHistory, customerProducts, products, onUpdateProduct]);

  if (!customer || !open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-background flex flex-col md:flex-row overflow-hidden"
      dir="rtl"
    >
      {/* Sidebar Navigation Component */}
      <CustomerPaymentSidebar
        user={user}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        onClose={onClose}
        onNavigate={onNavigate}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Header Bar Component */}
        <CustomerPaymentHeader
          customer={customer}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
          onClose={onClose}
        />

        {/* Content Body */}
        <div
          ref={scrollContainerRef}
          className="overflow-y-auto flex-1 p-3 md:p-6 space-y-3 md:space-y-4"
          data-scrollable="true"
          style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}
        >
          {/* Summary Stats Cards Component */}
          <CustomerPaymentStats
            customerProducts={customerProducts}
            activeTab={activeTab}
          />

          {/* Tabs Section */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" dir="rtl">
            <TabsList className="w-full h-auto p-1 bg-muted/50 rounded-lg border gap-1 grid grid-cols-4">
              <TabsTrigger
                value="payment"
                className="relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-muted-foreground transition-all duration-200 cursor-pointer hover:text-foreground hover:bg-background/60 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
              >
                <CreditCard className="h-4 w-4" />
                <span>پارەدان</span>
              </TabsTrigger>
              <TabsTrigger
                value="history"
                className="relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-muted-foreground transition-all duration-200 cursor-pointer hover:text-foreground hover:bg-background/60 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
              >
                <History className="h-4 w-4" />
                <span>مێژوو</span>
              </TabsTrigger>
              <TabsTrigger
                value="products"
                className="relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-muted-foreground transition-all duration-200 cursor-pointer hover:text-foreground hover:bg-background/60 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
              >
                <Package className="h-4 w-4" />
                <span>کاڵاکان</span>
              </TabsTrigger>
              <TabsTrigger
                value="completed"
                className="relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-muted-foreground transition-all duration-200 cursor-pointer hover:text-foreground hover:bg-background/60 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground data-[state=active]:shadow-md"
              >
                <CheckCircle className="h-4 w-4" />
                <span>تەواوبوو</span>
              </TabsTrigger>
            </TabsList>

            <div className="mt-3 rounded-lg border bg-card p-3 md:p-4" dir="rtl">
              {/* Tab 1: Products */}
              <TabsContent value="products" className="mt-0 space-y-2 md:space-y-4">
                {!hideAddProduct && (
                  <div className="flex items-center justify-between mb-3">
                    <AddProductDialog
                      customerId={customer.id}
                      onAdd={onAddProduct}
                      trigger={
                        <Button size="sm" className="h-8 md:h-9 text-xs md:text-sm">
                          <Plus className="h-3 w-3 md:h-4 md:w-4 ml-1 md:mr-2" />
                          <span>زیادکردنی کاڵا</span>
                        </Button>
                      }
                    />
                  </div>
                )}

                {activeProducts.length === 0 ? (
                  <div className="text-center py-8 md:py-12 text-muted-foreground border rounded-lg text-sm">
                    هیچ کاڵایەک نییە
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-3 pb-2 border-b">
                      <span className="text-sm text-muted-foreground">کۆی کاڵاکان: <span className="font-medium text-foreground">{activeProducts.length}</span></span>
                    </div>

                    {/* Desktop Table */}
                    <div className="rounded-md border hidden md:block" dir="rtl">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-right w-12">#</TableHead>
                            <TableHead className="text-right">ناوی ئامێر</TableHead>
                            <TableHead className="text-right">نرخی فرۆشتن</TableHead>
                            <TableHead className="text-right">وەرگیراو</TableHead>
                            <TableHead className="text-right">ماوە</TableHead>
                            <TableHead className="text-right">قیستی مانگانە</TableHead>
                            <TableHead className="text-right">بەروار</TableHead>
                            <TableHead className="text-right">تێبینی</TableHead>
                            <TableHead className="text-right w-24">کردارەکان</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {activeProducts.map((product, index) => (
                            <TableRow key={product.id} className={index % 2 === 1 ? 'bg-muted/50' : ''}>
                              <TableCell className="text-center font-medium text-muted-foreground">
                                {index + 1}
                              </TableCell>
                              <TableCell>
                                <div>
                                  <span className="font-medium">{product.deviceName}</span>
                                  {product.deviceDescription && (
                                    <p className="text-sm text-muted-foreground">{product.deviceDescription}</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1.5 justify-start">
                                  <span className="font-semibold" dir="ltr">{product.salePrice.toLocaleString()}</span>
                                  <Badge variant="outline" className="text-[10px] px-1.5 h-5">
                                    {product.currency === "IQD" ? "د.ع" : "$"}
                                  </Badge>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1.5 justify-start">
                                  <span className="font-semibold text-green-600" dir="ltr">{product.paidAmount.toLocaleString()}</span>
                                  <Badge variant="secondary" className="text-[10px] px-1.5 h-5">
                                    {product.currency === "IQD" ? "د.ع" : "$"}
                                  </Badge>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1.5 justify-start">
                                  <span className="font-semibold text-red-500" dir="ltr">{product.remainingAmount.toLocaleString()}</span>
                                  <Badge variant="destructive" className="text-[10px] px-1.5 h-5 text-white">
                                    {product.currency === "IQD" ? "د.ع" : "$"}
                                  </Badge>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1.5 justify-start">
                                  <span className="font-semibold" dir="ltr">{product.monthlyInstallment.toLocaleString()}</span>
                                  <Badge variant="outline" className="text-[10px] px-1.5 h-5">
                                    {product.currency === "IQD" ? "د.ع" : "$"}
                                  </Badge>
                                </div>
                              </TableCell>
                              <TableCell className="text-muted-foreground"><span dir="ltr">{product.date}</span></TableCell>
                              <TableCell className="max-w-[140px]" title={product.notes || "-"}>
                                <div className="text-xs text-muted-foreground truncate">
                                  {product.notes ? (
                                    <span className="bg-amber-50 dark:bg-amber-950/30 px-2 py-1 rounded border border-amber-200 dark:border-amber-900 inline-block max-w-full truncate">
                                      {product.notes}
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground/50">-</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center justify-center gap-1">
                                  <EditProductDialog product={product} onUpdate={onUpdateProduct} />
                                  {(product.paidAmount > 0 || (product.installments || 0) > 0) ? (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      disabled
                                      title="ناتوانرێت بسڕێتەوە (پارەدان کراوە)"
                                    >
                                      <Trash2 className="h-4 w-4 text-muted-foreground/50" />
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 bg-destructive/10 text-destructive hover:bg-destructive/20"
                                      onClick={async () => {
                                        await confirm({
                                          title: "سڕینەوەی کاڵا",
                                          description: "دڵنیایت لە سڕینەوەی ئەم کاڵایە؟",
                                          variant: "destructive",
                                          confirmText: "بەڵێ، بیسڕەوە",
                                          cancelText: "نەخێر",
                                          onConfirm: async () => {
                                            await onDeleteProduct(product.id);
                                          }
                                        });
                                      }}
                                      title="سڕینەوە"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-3">
                      {activeProducts.map((product, index) => {
                        const isExpanded = expandedProducts.has(product.id);
                        const isEven = index % 2 === 1;
                        return (
                          <div key={product.id} className={`rounded-xl border overflow-hidden transition-all duration-200 ${isExpanded ? 'border-primary ring-1 ring-primary/20' : ''} ${isEven ? 'bg-muted/60' : 'bg-card'}`}>
                            <div className="p-3 cursor-pointer" onClick={() => toggleExpanded(product.id)}>
                              <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                  <span className="text-sm font-bold text-primary">{index + 1}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-sm truncate">{product.deviceName}</span>
                                    <Badge variant={product.currency === "IQD" ? "outline" : "secondary"} className="text-[10px] px-1.5 h-4">
                                      {product.currency === "IQD" ? "د.ع" : "$"}
                                    </Badge>
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-0.5">
                                    {product.deviceDescription || product.date}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                  <EditProductDialog product={product} onUpdate={onUpdateProduct} />
                                  {(product.paidAmount > 0 || (product.installments || 0) > 0 || paymentHistory.some(p => p.productId === product.id)) ? (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      disabled
                                      title="ناتوانرێت بسڕێتەوە (پارەدان کراوە)"
                                    >
                                      <Trash2 className="h-4 w-4 text-muted-foreground/50" />
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 bg-destructive/10 text-destructive hover:bg-destructive/20"
                                      onClick={async () => {
                                        await confirm({
                                          title: "سڕینەوەی کاڵا",
                                          description: "دڵنیایت لە سڕینەوەی ئەم کاڵایە؟",
                                          variant: "destructive",
                                          confirmText: "بەڵێ، بیسڕەوە",
                                          cancelText: "نەخێر",
                                          onConfirm: async () => {
                                            await onDeleteProduct(product.id);
                                          }
                                        });
                                      }}
                                      title="سڕینەوە"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => toggleExpanded(product.id)}
                                  >
                                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                  </Button>
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-1 mt-2 text-xs bg-muted/30 rounded-lg p-2" dir="rtl">
                                <div className="text-center">
                                  <div className="text-muted-foreground text-[10px]">نرخ</div>
                                  <div className="font-semibold" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.salePrice.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></> : <><span className="text-[10px] text-muted-foreground">$ </span>{product.salePrice.toLocaleString()}</>}</div>
                                </div>
                                <div className="text-center border-x border-dashed">
                                  <div className="text-muted-foreground text-[10px]">وەرگیراو</div>
                                  <div className="font-semibold text-green-600" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.paidAmount.toLocaleString()} <span className="text-[10px]">د.ع</span></> : <><span className="text-[10px]">$ </span>{product.paidAmount.toLocaleString()}</>}</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-muted-foreground text-[10px]">ماوە</div>
                                  <div className="font-semibold text-red-500" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.remainingAmount.toLocaleString()} <span className="text-[10px]">د.ع</span></> : <><span className="text-[10px]">$ </span>{product.remainingAmount.toLocaleString()}</>}</div>
                                </div>
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="px-3 pb-3 pt-0 border-t bg-muted/30" dir="rtl">
                                <div className="space-y-2 pt-3">
                                  <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                    <span className="text-muted-foreground text-xs">قیستی مانگانە</span>
                                    <span className="font-medium text-sm" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.monthlyInstallment.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></> : <><span className="text-[10px] text-muted-foreground">$ </span>{product.monthlyInstallment.toLocaleString()}</>}</span>
                                  </div>
                                  <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                    <span className="text-muted-foreground text-xs">ژمارەی قیستەکان</span>
                                    <span className="font-medium text-sm">{product.installments || 0}</span>
                                  </div>
                                  <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                    <span className="text-muted-foreground text-xs">بەروار</span>
                                    <span className="font-medium text-sm" dir="ltr">{product.date}</span>
                                  </div>
                                  {product.deviceDescription && (
                                    <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                      <span className="text-muted-foreground text-xs">وەسف</span>
                                      <span className="font-medium text-sm">{product.deviceDescription}</span>
                                    </div>
                                  )}
                                  {product.notes && (
                                    <div className="pt-2 mt-1 border-t">
                                      <span className="text-muted-foreground text-xs block mb-1">تێبینی</span>
                                      <p className="text-sm bg-background rounded-lg p-2 text-muted-foreground">{product.notes}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </TabsContent>

              {/* Tab 2: Payment Form Cards (ProductPaymentCard) */}
              <TabsContent value="payment" className="mt-0 space-y-4">
                {activeProducts.length === 0 ? (
                  <div className="text-center py-6 md:py-8 text-muted-foreground border rounded-lg text-xs md:text-sm">
                    هیچ کاڵایەک نییە
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activeProducts.map((product, index) => (
                      <ProductPaymentCard
                        key={product.id}
                        product={product}
                        index={index}
                        customer={customer}
                        paymentHistory={paymentHistory}
                        globalPaymentDate={globalPaymentDate}
                        persistentNote={productPersistentNotes[product.id]}
                        noteSaveStatus={productNoteSaveStatus[product.id]}
                        onSavePersistentNote={savePersistentNote}
                        onPay={handlePaySingleProduct}
                        confirm={confirm}
                        showToast={showToast}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Tab 3: Payment History */}
              <TabsContent value="history" className="mt-0 space-y-3 md:space-y-4">
                <PaymentHistoryTable
                  paymentHistory={paymentHistory}
                  isLoadingPayments={isLoadingPayments}
                  products={customerProducts.length > 0 ? customerProducts : products}
                  onPrintPayment={printPaymentFromHistory}
                  onDeletePayment={handleDeletePayment}
                  confirm={confirm}
                />
              </TabsContent>

              {/* Tab 4: Completed Products */}
              <TabsContent value="completed" className="mt-0 space-y-2 md:space-y-4">
                {completedProducts.length === 0 ? (
                  <div className="text-center py-8 md:py-12 text-muted-foreground border rounded-lg text-sm">
                    هیچ کاڵایەکی تەواوبوو نییە
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-3 pb-2 border-b">
                      <span className="text-sm text-muted-foreground">کۆی کاڵا تەواوبووەکان: <span className="font-medium text-foreground">{completedProducts.length}</span></span>
                    </div>

                    {/* Desktop Table for Completed */}
                    <div className="rounded-md border hidden md:block" dir="rtl">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-right w-12">#</TableHead>
                            <TableHead className="text-right">ناوی ئامێر</TableHead>
                            <TableHead className="text-right">نرخی فرۆشتن</TableHead>
                            <TableHead className="text-right">وەرگیراو</TableHead>
                            <TableHead className="text-right">بەروار</TableHead>
                            <TableHead className="text-right">تێبینی</TableHead>
                            <TableHead className="text-right w-24">دۆخ</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {completedProducts.map((product, index) => (
                            <TableRow key={product.id} className={index % 2 === 1 ? 'bg-muted/50' : ''}>
                              <TableCell className="text-center font-medium text-muted-foreground">
                                {index + 1}
                              </TableCell>
                              <TableCell>
                                <div>
                                  <span className="font-medium">{product.deviceName}</span>
                                  {product.deviceDescription && (
                                    <p className="text-sm text-muted-foreground">{product.deviceDescription}</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1.5 justify-start">
                                  <span className="font-semibold" dir="ltr">{product.salePrice.toLocaleString()}</span>
                                  <Badge variant="outline" className="text-[10px] px-1.5 h-5">
                                    {product.currency === "IQD" ? "د.ع" : "$"}
                                  </Badge>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1.5 justify-start">
                                  <span className="font-semibold text-green-600" dir="ltr">{product.paidAmount.toLocaleString()}</span>
                                  <Badge variant="secondary" className="text-[10px] px-1.5 h-5">
                                    {product.currency === "IQD" ? "د.ع" : "$"}
                                  </Badge>
                                </div>
                              </TableCell>
                              <TableCell className="text-muted-foreground"><span dir="ltr">{product.date}</span></TableCell>
                              <TableCell className="max-w-[140px]" title={product.notes || "-"}>
                                <div className="text-xs text-muted-foreground truncate">
                                  {product.notes || "-"}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="default" className="bg-green-600">تەواوبوو</Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Mobile Cards for Completed */}
                    <div className="md:hidden space-y-3">
                      {completedProducts.map((product, index) => {
                        const isExpanded = expandedCompletedProducts.has(product.id);
                        return (
                          <div key={product.id} className="rounded-xl border bg-card overflow-hidden">
                            <div className="p-3 cursor-pointer" onClick={() => toggleCompletedProductExpanded(product.id)}>
                              <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                                  <CheckCircle className="h-5 w-5 text-green-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-sm truncate">{product.deviceName}</span>
                                    <Badge variant="outline" className="text-[10px] px-1.5 h-4">
                                      {product.currency === "IQD" ? "د.ع" : "$"}
                                    </Badge>
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-0.5">
                                    {product.deviceDescription || product.date}
                                  </div>
                                </div>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                </Button>
                              </div>
                              <div className="grid grid-cols-3 gap-1 mt-2 text-xs bg-muted/30 rounded-lg p-2" dir="rtl">
                                <div className="text-center">
                                  <div className="text-muted-foreground text-[10px]">نرخ</div>
                                  <div className="font-semibold" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.salePrice.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></> : <><span className="text-[10px] text-muted-foreground">$ </span>{product.salePrice.toLocaleString()}</>}</div>
                                </div>
                                <div className="text-center border-x border-dashed">
                                  <div className="text-muted-foreground text-[10px]">وەرگیراو</div>
                                  <div className="font-semibold text-green-600" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.paidAmount.toLocaleString()} <span className="text-[10px]">د.ع</span></> : <><span className="text-[10px]">$ </span>{product.paidAmount.toLocaleString()}</>}</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-muted-foreground text-[10px]">دۆخ</div>
                                  <Badge variant="default" className="text-[10px] px-1.5 h-4 bg-green-600">تەواو</Badge>
                                </div>
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="px-3 pb-3 pt-0 border-t bg-muted/30" dir="rtl">
                                <div className="space-y-2 pt-3">
                                  <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                    <span className="text-muted-foreground text-xs">قیستی مانگانە</span>
                                    <span className="font-medium text-sm" dir={product.currency === "IQD" ? "rtl" : "ltr"}>{product.currency === "IQD" ? <>{product.monthlyInstallment.toLocaleString()} <span className="text-[10px] text-muted-foreground">د.ع</span></> : <><span className="text-[10px] text-muted-foreground">$ </span>{product.monthlyInstallment.toLocaleString()}</>}</span>
                                  </div>
                                  <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                    <span className="text-muted-foreground text-xs">ژمارەی قیستەکان</span>
                                    <span className="font-medium text-sm">{product.installments || 0}</span>
                                  </div>
                                  {product.deviceDescription && (
                                    <div className="flex justify-between items-center py-1.5 border-b border-dashed">
                                      <span className="text-muted-foreground text-xs">وەسف</span>
                                      <span className="font-medium text-sm">{product.deviceDescription}</span>
                                    </div>
                                  )}
                                  {product.notes && (
                                    <div className="pt-2 mt-1 border-t">
                                      <span className="text-muted-foreground text-xs block mb-1">تێبینی</span>
                                      <p className="text-sm bg-muted/50 rounded-lg p-2 text-muted-foreground">{product.notes}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>

      {/* Payment Confirmation Dialog */}
      {showPaymentConfirmation && pendingPaymentAction && (
        <PaymentConfirmationDialog
          open={showPaymentConfirmation}
          onOpenChange={setShowPaymentConfirmation}
          onConfirm={handleConfirmPayment}
          totalAmount={pendingPaymentAction.paymentsToAdd.reduce((sum, p) => sum + p.amount, 0)}
          currency={pendingPaymentAction.paymentsToAdd[0]?.currency || "IQD"}
          productCount={pendingPaymentAction.productsToUpdate.length}
          date={globalPaymentDate}
          withPrint={pendingPaymentAction.withPrint}
        />
      )}

      {/* Hidden Print Invoice Element */}
      {invoiceData && customer && (
        <div className="fixed left-[-9999px] top-0">
          <PaymentInvoice
            ref={invoiceRef}
            customer={customer}
            payments={invoiceData.payments}
            products={products}
            invoiceNumber={invoiceData.invoiceNumber}
            paymentDate={invoiceData.paymentDate}
            settings={shopSettings}
          />
        </div>
      )}
    </div>
  );
}
